import {
	ActionIcon,
	Badge,
	Box,
	Button,
	Group,
	Menu,
	Stack,
	Text,
	Tooltip,
	UnstyledButton,
} from "@mantine/core";
import {useMediaQuery} from "@mantine/hooks";
import {useColorScheme} from "@mui/material";
import {
	IconChevronDown,
	IconDroplet,
	IconHelp,
	IconLeaf,
	IconMenu2,
	IconMoon,
	IconUser,
} from "@tabler/icons-react";
import {Link as RouterLink} from "react-router";
import {useAuth} from "@hooks/useAuth.tsx";
import {usePopUp} from "@hooks/usePopUp.tsx";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {AppDrawer} from "./AppDrawer.tsx";

import logo from "/BalyGo-halloween.svg";
import logoInverted from "/BalyGo-inverted-halloween.svg";
import {LoginPage} from "@components/account/LoginPage.tsx";
import {menuItems} from "@components/default/MenuItems.tsx";
import {RequiredPermission} from "@components/default/Permissionable.tsx";
import {FirstRunSelectTheme} from "@components/default/FirstRunSelectTheme.tsx";

export interface NavItem {
	text: string;
	icon: React.ReactNode;
	path?: string;
	children?: NavItem[];
	hide?: "without permission" | boolean;
	permissions?: RequiredPermission[];
}

function RenderMenuItemNode({item}: { item: NavItem }) {
	const {permissions, hasPermission} = useAuth();
	const shouldHide = useCallback(
		({it}: { it: NavItem }) => {
			return (it.hide === "without permission" && !hasPermission(it.permissions)) || it.hide === true;
		},
		[permissions, hasPermission],
	);

	if (shouldHide({it: item})) return null;

	const isDisabled =
		item.permissions != null &&
		!item.permissions.some((p) => permissions?.some((p2) => p.modulo === p2.modulo));

	if (item.children && item.children.length > 0) {
		return (
			<Menu.Sub key={item.text}>
				<Menu.Sub.Target>
					<Menu.Sub.Item
						leftSection={item.icon}
						disabled={isDisabled}
					>
						{item.text}
					</Menu.Sub.Item>
				</Menu.Sub.Target>
				<Menu.Sub.Dropdown>
					{item.children.map((child) => (
						<RenderMenuItemNode key={child.text} item={child} />
					))}
				</Menu.Sub.Dropdown>
			</Menu.Sub>
		);
	}

	return (
		<Menu.Item
			key={item.text}
			leftSection={item.icon}
			component={item.path ? RouterLink : "button"}
			to={item.path}
			disabled={isDisabled}
		>
			{item.text}
		</Menu.Item>
	);
}

function DesktopMenuItem({item, textColor}: { item: NavItem; textColor?: string }) {
	const {permissions, hasPermission} = useAuth();

	const shouldHide = useCallback(
		({it}: { it: NavItem }) => {
			return (it.hide === "without permission" && !hasPermission(it.permissions)) || it.hide === true;
		},
		[item, permissions, hasPermission],
	);

	const hidden = useMemo(() => {
		return shouldHide({it: item});
	}, [item, permissions, hasPermission, shouldHide]);

	if (hidden) return null;

	const isDisabled =
		item.permissions != null &&
		!item.permissions.some((p) => permissions?.some((p2) => p.modulo === p2.modulo));

	if (item.children && item.children.length > 0) {
		return (
			<Menu shadow="md" width={220} trigger="click-hover" closeOnItemClick openDelay={50} closeDelay={150}>
				<Menu.Target>
					<Button
						variant="subtle"
						disabled={isDisabled}
						style={{color: textColor, textTransform: "none"}}
						rightSection={<IconChevronDown size={18} />}
						leftSection={item.icon}
					>
						{item.text}
					</Button>
				</Menu.Target>
				<Menu.Dropdown>
					{item.children.map((child) => (
						<RenderMenuItemNode key={child.text} item={child} />
					))}
				</Menu.Dropdown>
			</Menu>
		);
	}

	return (
		<Button
			variant="subtle"
			component={RouterLink}
			to={item.path!}
			style={{color: textColor, textTransform: "none"}}
			leftSection={item.icon}
		>
			{item.text}
		</Button>
	);
}

export function AppHeader() {
	const {isAuthenticated, user, token, logout} = useAuth();
	const {addAlert, addPopUp, closePopUp} = usePopUp();
	const {colorScheme, setColorScheme, setMode} = useColorScheme();
	const isMobile = useMediaQuery("(max-width: 900px)");
	const [drawerOpen, setDrawerOpen] = useState(false);
	const firstRunChecked = useRef(false);

	const handleDrawerToggle = () => {
		setDrawerOpen(!drawerOpen);
	};

	useEffect(() => {
		if (firstRunChecked.current) return;
		firstRunChecked.current = true;

		const val = localStorage.getItem("userThemeSelected");
		if (val == null) {
			addPopUp({title: "Selecione um tema", content: <FirstRunSelectTheme />});
		}
	}, [addPopUp]);

	const isTropical = (colorScheme as string) === "tropical";
	const isIntencion = (colorScheme as string) === "intencion";
	const isMasterGold = (colorScheme as string) === "masterGold";

	const headerBg = isTropical ? "#fdd835" : isIntencion ? "#2B3292" : "#1E1E1E";
	const headerTextColor = isTropical ? "#121212" : "#FFFFFF";

	const cycleTheme = () => {
		switch (colorScheme as "masterGold" | "intencion" | "tropical") {
			case "masterGold":
				setMode("light");
				setColorScheme("intencion" as any);
				break;
			case "intencion":
				setMode("light");
				setColorScheme("tropical" as any);
				break;
			case "tropical":
				setMode("dark");
				setColorScheme("masterGold" as any);
				break;
			default:
				setMode("light");
				setColorScheme("tropical" as any);
				break;
		}
	};

	return (
		<>
			<Box
				component="header"
				className="fixed-print"
				style={{
					position: "sticky",
					top: 0,
					zIndex: 100,
					backgroundColor: headerBg,
					color: headerTextColor,
					opacity: 0.98,
					backdropFilter: "blur(8px)",
					boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
					transition: "background-color 0.3s ease, color 0.3s ease",
					width: "100%",
				}}
			>
				<Box px="md" style={{height: 64, display: "flex", alignItems: "center"}}>
					<Group justify="space-between" align="center" w="100%" wrap="nowrap">
						{/* Logo and Mobile Menu Toggle */}
						<Group align="center" gap="xs" wrap="nowrap">
							<ActionIcon
								variant="subtle"
								size="lg"
								className="no-print"
								aria-label="open drawer"
								onClick={handleDrawerToggle}
								style={{color: headerTextColor}}
							>
								<IconMenu2 size={22} />
							</ActionIcon>

							<UnstyledButton
								component={RouterLink}
								to="/"
								style={{display: "flex", alignItems: "center", position: "relative"}}
							>
								<img
									src={isTropical ? logoInverted : logo}
									alt="Logo"
									style={{
										height: 60,
										backgroundColor: import.meta.env.DEV ? "red" : undefined,
									}}
								/>
								{import.meta.env.DEV && (
									<Badge
										size="xs"
										color="red"
										variant="filled"
										style={{position: "absolute", bottom: -2, right: 0}}
									>
										HOMOLOGAÇÃO
									</Badge>
								)}
							</UnstyledButton>
						</Group>

						{/* Desktop Menu Items */}
						{!isMobile && user != null && (
							<Group gap="xs" ml="md" className="no-print" wrap="nowrap">
								{menuItems.map((item) => (
									<DesktopMenuItem key={item.text} item={item} textColor={headerTextColor} />
								))}
							</Group>
						)}

						{/* Spacer */}
						<Box style={{flex: 1}} />

						{/* Right-aligned items: Theme toggle, Login/Logout/Token */}
						<Group gap="xs" align="center" wrap="nowrap">
							{/* Theme Toggle Button */}
							{!isMobile && (
								<Tooltip label="Alternar tema">
									<ActionIcon
										variant="subtle"
										size="lg"
										className="no-print"
										onClick={cycleTheme}
										style={{color: headerTextColor}}
									>
										{isMasterGold ? (
											<IconMoon size={22} />
										) : isIntencion ? (
											<IconDroplet size={22} color="#42a5f5" />
										) : isTropical ? (
											<IconLeaf size={22} color="#e65100" />
										) : (
											<IconHelp size={22} />
										)}
									</ActionIcon>
								</Tooltip>
							)}

							{/* Auth section */}
							{!isAuthenticated ? (
								<Button
									variant="subtle"
									color="green"
									leftSection={<IconUser size={18} />}
									style={{color: isTropical ? "#1b5e20" : "#81C784"}}
									onClick={() => {
										addPopUp({title: "Login", content: <LoginPage />});
									}}
								>
									Login
								</Button>
							) : (
								<Group gap="xs" align="center" wrap="nowrap">
									<Tooltip label={`Desconectar de ${user?.userName}`}>
										<Button
											variant="subtle"
											color="yellow"
											className="no-print"
											leftSection={<IconUser size={18} />}
											style={{color: isTropical ? "#b78103" : "#FDD835"}}
											onClick={() => {
												addPopUp({
													title: "Deseja desconectar?",
													content: (
														<Stack gap="md">
															<Text size="sm">Tem certeza que deseja desconectar?</Text>
															<Group justify="flex-end">
																<Button
																	color="red"
																	variant="filled"
																	onClick={() => {
																		logout();
																		closePopUp();
																	}}
																>
																	Sim, Desconectar
																</Button>
															</Group>
														</Stack>
													),
												});
											}}
										>
											{!isMobile && `Desconectar (${user?.userName})`}
										</Button>
									</Tooltip>

									{import.meta.env.DEV && (
										<Tooltip label="Copiar token">
											<Button
												variant="subtle"
												color="blue"
												className="no-print"
												leftSection={<IconUser size={18} />}
												style={{color: isTropical ? "#0d47a1" : "#64B5F6"}}
												onClick={() => {
													if (token != null) {
														try {
															navigator.clipboard.writeText(token).then(() => {
																addAlert({
																	content: "Token copiado para área de transferência (Ctrl+V)",
																	severity: "info",
																	time: 2500,
																});
															});
														} catch {
															addAlert({
																content: "Copiar token não suportado pelo navegador ou sem SSL",
																severity: "warning",
															});
														}
													}
												}}
											>
												{!isMobile && "Token"}
											</Button>
										</Tooltip>
									)}
								</Group>
							)}
						</Group>
					</Group>
				</Box>
			</Box>
			<AppDrawer open={drawerOpen} onClose={handleDrawerToggle} />
		</>
	);
}